import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { isRtlLanguage } from "./Components/languages";
import {
  applyDocumentLanguage,
  getStoredLanguage,
  VISITOR_LANGUAGE_CHANGE_EVENT,
} from "./Components/translate";
import { getAuthToken } from "./Components/authSession";
import { startLocationTracker, stopLocationTracker } from "./Components/locationTracker";
import WorkStartPromptGate from "./Components/WorkStartPromptGate";
import EmployeeMessageGate from "./Components/EmployeeMessageGate";
import Login from "./Login";
import VisitorChrome from "./Components/VisitorChrome";
import ScrollToTop from "./Components/ScrollToTop";
import Shop from "./Shop";
import ShopProducts from "./panel/shop/[shopId]";
import ShowFactor from "./panel/factor/[factorId]";
import Cart from "./panel/Cart";
import { ShopListSkeleton } from "./Components/SkeletonLoading";
import { VisitorLanguageProvider } from "./Components/useVisitorLanguage";
import axios from "axios";
import { baseUrl } from "./Components/baseUrl";
import { runVisitorBootstrap } from "./Components/visitorBootstrap";

const EditOrder = React.lazy(() => import("./panel/edit_order/[orderId]"));
const Notifications = React.lazy(() => import("./panel/Notifications"));
const Canceled = React.lazy(() => import("./panel/orders/Canceled"));
const Confirmed = React.lazy(() => import("./panel/orders/Confirmed"));
const NewOrders = React.lazy(() => import("./panel/orders/NewOrders"));
const Ongoing = React.lazy(() => import("./panel/orders/Ongoing"));
const PayList = React.lazy(() => import("./panel/orders/PayList"));
const Returned = React.lazy(() => import("./panel/orders/Returned"));
const Research = React.lazy(() => import("./panel/Research"));
const Setting = React.lazy(() => import("./panel/Setting"));
const Profile = React.lazy(() => import("./panel/Profile"));
const ReturnOrder = React.lazy(() => import("./panel/return/[orderId]"));
const CustomerReturnOrder = React.lazy(() => import("./panel/customer-return/[customerId]"));
const PayInfo = React.lazy(() => import("./panel/pay/[payId]"));
const CalendarPage = React.lazy(() => import("./panel/Calendar"));
const ComebackOrder = React.lazy(() => import("./panel/comeback/[orderId]"));
const LocationEnable = React.lazy(() => import("./panel/LocationEnable"));

const AddShop = React.lazy(() => import("./panel/AddShop"));
const Reports = React.lazy(() => import("./panel/Reports"));
const HomeMap = React.lazy(() => import("./panel/Map"));
const CalendarShopProducts = React.lazy(() => import("./panel/calendar/[shopId]"));
const EditShop = React.lazy(() => import("./panel/edit/[shopId]"));
const OnlyShowFactor = React.lazy(() => import("./panel/show_factor/[factorId]"));
const Payment = React.lazy(() => import("./panel/payment/[factorId]"));

const LazyRoute = ({ children }) => (
  <Suspense fallback={<ShopListSkeleton count={3} />}>{children}</Suspense>
);

function App() {
    const [toastRtl, setToastRtl] = useState(() =>
        isRtlLanguage(getStoredLanguage())
    );

    useEffect(() => {
        applyDocumentLanguage(getStoredLanguage());

        if (getAuthToken()) {
            startLocationTracker();
            runVisitorBootstrap(axios, baseUrl).catch(() => null);
        } else {
            stopLocationTracker();
        }

        const handleLanguageChange = (event) => {
            const lang = event.detail?.lang || getStoredLanguage();
            applyDocumentLanguage(lang);
            setToastRtl(isRtlLanguage(lang));
        };

        window.addEventListener(
            VISITOR_LANGUAGE_CHANGE_EVENT,
            handleLanguageChange
        );

        return () => {
            window.removeEventListener(
                VISITOR_LANGUAGE_CHANGE_EVENT,
                handleLanguageChange
            );
            stopLocationTracker();
        };
    }, []);

    return (
        <div className="App">
            <ToastContainer
                rtl={toastRtl}
                position="top-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                pauseOnFocusLoss={false}
                draggable
                pauseOnHover={false}
            />
            <BrowserRouter>
                <VisitorLanguageProvider>
                <ScrollToTop />
                <WorkStartPromptGate />
                <EmployeeMessageGate />
                <VisitorChrome />
                <Routes>
                    <Route>
                        <Route path="/login" element={<Login />} />
                        <Route path="/panel/enable-location" element={<LazyRoute><LocationEnable /></LazyRoute>} />
                        <Route path="/" element={<Shop />} />
                        <Route
                            path="/products/:shopId"
                            element={<ShopProducts />}
                        />
                        <Route
                            path="/calendar/:shopId"
                            element={
                                <LazyRoute>
                                    <CalendarShopProducts />
                                </LazyRoute>
                            }
                        />

                        <Route path="/panel/setting" element={<LazyRoute><Setting /></LazyRoute>} />
                        <Route
                            path="/panel/calendar"
                            element={<LazyRoute><CalendarPage /></LazyRoute>}
                        />
                        <Route
                            path="/panel/add_shop"
                            element={
                                <LazyRoute>
                                    <AddShop />
                                </LazyRoute>
                            }
                        />
                        <Route
                            path="/panel/reports"
                            element={
                                <LazyRoute>
                                    <Reports />
                                </LazyRoute>
                            }
                        />
                        <Route
                            path="/panel/notifications"
                            element={<LazyRoute><Notifications /></LazyRoute>}
                        />
                        <Route path="/panel/research" element={<LazyRoute><Research /></LazyRoute>} />
                        <Route path="/panel/cart" element={<Cart />} />
                        <Route path="/panel/profile" element={<LazyRoute><Profile /></LazyRoute>} />
                        <Route
                            path="/panel/orders/new_orders"
                            element={<LazyRoute><NewOrders /></LazyRoute>}
                        />
                        <Route
                            path="/panel/orders/ongoing"
                            element={<LazyRoute><Ongoing /></LazyRoute>}
                        />
                        <Route
                            path="/panel/map"
                            element={
                                <LazyRoute>
                                    <HomeMap />
                                </LazyRoute>
                            }
                        />
                        <Route
                            path="/panel/orders/confirmed"
                            element={<LazyRoute><Confirmed /></LazyRoute>}
                        />
                        <Route
                            path="/panel/orders/returned"
                            element={<LazyRoute><Returned /></LazyRoute>}
                        />
                        <Route
                            path="/panel/orders/canceled"
                            element={<LazyRoute><Canceled /></LazyRoute>}
                        />
                        <Route
                            path="/panel/orders/pay_list"
                            element={<LazyRoute><PayList /></LazyRoute>}
                        />
                        <Route
                            path="/panel/edit/:shopId"
                            element={
                                <LazyRoute>
                                    <EditShop />
                                </LazyRoute>
                            }
                        />
                        <Route
                            path="/panel/edit_order/:orderId"
                            element={<LazyRoute><EditOrder /></LazyRoute>}
                        />
                        <Route
                            path="/panel/return/:orderId"
                            element={<LazyRoute><ReturnOrder /></LazyRoute>}
                        />
                        <Route
                            path="/panel/customer-return/:customerId"
                            element={<LazyRoute><CustomerReturnOrder /></LazyRoute>}
                        />
                        <Route
                            path="/panel/comeback/:orderId"
                            element={<LazyRoute><ComebackOrder /></LazyRoute>}
                        />
                        <Route
                            path="/panel/factor/:factorId"
                            element={<ShowFactor />}
                        />
                        <Route
                            path="/panel/show_factor/:factorId"
                            element={
                                <LazyRoute>
                                    <OnlyShowFactor />
                                </LazyRoute>
                            }
                        />
                        <Route
                            path="/panel/payment/:factorId"
                            element={
                                <LazyRoute>
                                    <Payment />
                                </LazyRoute>
                            }
                        />

                        <Route path="/panel/pay/:payId" element={<LazyRoute><PayInfo /></LazyRoute>} />
                    </Route>
                </Routes>
                </VisitorLanguageProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
